/**
 * Fetches every product owned by the current buyer and joins it against the
 * full list of linked LoanApplications to produce a `PropertyView[]`. The
 * computed list respects the user's chosen filter + sort from
 * `propertyUiSlice`.
 *
 * For sellers, listings come from SellerContract records instead of products.
 *
 * PropertyView itself is **derived** — see `src/utils/propertyView/buildPropertyView.ts`.
 */

import { useMemo } from 'react';
import type { RootState } from '@/store';
import { useSelector } from 'react-redux';

import { USER_ROLE } from '@/config/constants';
import { useGetMyLoanApplicationsQuery } from '@/apis/extended/loanApplicationApi';
import { useGetProductsQuery } from '@/apis/extended/productApi';
import { useGetAllSellerContractsQuery } from '@/apis/extended/sellerAPI';
import { useGetCurrentUserQuery } from '@/apis/extended/userApi';
import type {
  PropertyFilter,
  PropertySort,
} from '@/store/slices/propertyUiSlice';
import { pickApplicationFor } from '@/utils/loanApplicationPick';
import { buildPropertyView } from '@/utils/propertyView/buildPropertyView';
import { resolveSellerStage } from '@/utils/propertyView/sellerStageResolver';
import {
  dedupeSellerContractRows,
  normalizeSellerContractRow,
} from '@/utils/sellerContract/normalizeSellerProduct';
import type { Product } from '@/types/ProductType';
import type { PropertyView } from '@/types/PropertyView';
import type { SellerContractRecord } from '@/types/SellerContractType';

function matchesFilter(p: PropertyView, filter: PropertyFilter): boolean {
  if (p.pipelineMode === 'seller') return filter === 'all';
  switch (filter) {
    case 'purchase':
      return !p.isRefinance;
    case 'refinance':
      return p.isRefinance;
    case 'dscr':
      return p.isDscr;
    default:
      return true;
  }
}

function sortFn(sort: PropertySort) {
  return (a: PropertyView, b: PropertyView) => {
    switch (sort) {
      case 'stage':
        return a.stage - b.stage;
      case 'price':
        return (b.listPrice ?? 0) - (a.listPrice ?? 0);
      case 'updated':
      default: {
        const aDate = new Date(a.stageEnteredAt).getTime();
        const bDate = new Date(b.stageEnteredAt).getTime();
        return bDate - aDate;
      }
    }
  };
}

function buildSellerProperties(
  rows: SellerContractRecord[] | undefined
): PropertyView[] {
  if (!rows?.length) return [];
  const deduped = dedupeSellerContractRows(rows, (c) =>
    resolveSellerStage({ contract: c })
  );
  return deduped.map((row) => {
    const { product, contract, contractId } = normalizeSellerContractRow(row);
    return buildPropertyView({
      product,
      contract,
      pipelineMode: 'seller',
      contractIdOverride: contractId,
    });
  });
}

export function useProperties(): {
  properties: PropertyView[];
  allProperties: PropertyView[];
  isLoading: boolean;
  /** Normalized product snapshots for seller listings (keyed by product id). */
  sellerProductsById: Map<string, Product>;
} {
  const { data: user } = useGetCurrentUserQuery();
  const isSeller = user?.role === USER_ROLE.SELLER;

  const { data: products, isLoading: productsLoading } = useGetProductsQuery(
    {},
    { skip: isSeller }
  );
  const { data: applications, isLoading: appsLoading } =
    useGetMyLoanApplicationsQuery(undefined, { skip: isSeller });
  const { data: sellerContracts, isLoading: sellerContractsLoading } =
    useGetAllSellerContractsQuery(user?._id ?? '', {
      skip: !isSeller || !user?._id,
    });

  const filter = useSelector((s: RootState) => s.propertyUi.filter);
  const sort = useSelector((s: RootState) => s.propertyUi.sort);

  const sellerProductsById = useMemo(() => {
    const map = new Map<string, Product>();
    if (!isSeller || !sellerContracts) return map;
    for (const row of sellerContracts as unknown as SellerContractRecord[]) {
      const { product } = normalizeSellerContractRow(row);
      map.set(product._id, product);
    }
    return map;
  }, [isSeller, sellerContracts]);

  const allProperties = useMemo<PropertyView[]>(() => {
    if (isSeller) {
      return buildSellerProperties(
        sellerContracts as unknown as SellerContractRecord[] | undefined
      );
    }
    const list = products?.products ?? [];
    const apps = applications ?? [];
    return list.map((product) =>
      buildPropertyView({
        product,
        application: pickApplicationFor(product, apps),
        user,
        pipelineMode: 'buyer',
      })
    );
  }, [isSeller, sellerContracts, products?.products, applications, user]);

  const properties = useMemo(() => {
    return [...allProperties]
      .filter((p) => matchesFilter(p, filter))
      .sort(sortFn(sort));
  }, [allProperties, filter, sort]);

  return {
    properties,
    allProperties,
    isLoading: isSeller
      ? sellerContractsLoading
      : productsLoading || appsLoading,
    sellerProductsById,
  };
}
